/**
 * SearchableCombo — Iter 391b
 *
 * Lightweight combobox: a text input filters an inline dropdown of
 * options. Use where a plain `<select>` becomes tedious once the list
 * grows (e.g. 15+ pieces of equipment, or 50+ parameters).
 *
 * Props
 *   • value       — current selected option id (or "" for none)
 *   • onChange    — (id) => void
 *   • options     — [{ id, label, group? }]  (group is optional
 *                   sub-heading rendered above the row)
 *   • placeholder — text shown when no value is set
 *   • allLabel    — label for the "all / clear" entry (defaults to "All")
 *   • testId      — data-testid prefix
 */
import { useState, useRef, useEffect } from "react";
import { ChevronDown, X, Search } from "lucide-react";

export default function SearchableCombo({
  value,
  onChange,
  options = [],
  placeholder = "Search…",
  allLabel = "All",
  testId = "combo",
  className = "",
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);
  const selected = options.find((o) => o.id === value);

  useEffect(() => {
    const onDoc = (e) => {
      if (!ref.current?.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const norm = (s) => (s || "").toLowerCase();
  const filtered = q
    ? options.filter((o) => norm(o.label).includes(norm(q)) || norm(o.group).includes(norm(q)))
    : options;

  return (
    <div ref={ref} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="hv-input text-xs py-1 flex items-center justify-between gap-1 w-full"
        data-testid={`${testId}-trigger`}
      >
        <span className="truncate">
          {selected ? selected.label : <span className="text-[#647087]">{allLabel}</span>}
        </span>
        <ChevronDown className="w-3 h-3 text-[#647087] shrink-0" />
      </button>
      {open && (
        <div
          className="absolute z-30 mt-1 left-0 right-0 min-w-[220px] hv-card p-1 max-h-[280px] overflow-y-auto shadow-2xl"
          data-testid={`${testId}-panel`}
        >
          <div className="flex items-center gap-1.5 px-2 py-1.5 border-b border-white/5 sticky top-0 bg-[var(--hv-surface,#121419)]">
            <Search className="w-3 h-3 text-[#647087] shrink-0" />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={placeholder}
              className="bg-transparent outline-none text-xs flex-1 placeholder:text-[#647087]"
              data-testid={`${testId}-input`}
              onKeyDown={(e) => {
                if (e.key === "Enter" && filtered.length) {
                  onChange(filtered[0].id);
                  setOpen(false);
                  setQ("");
                } else if (e.key === "Escape") {
                  setOpen(false);
                }
              }}
            />
            {value && (
              <button
                type="button"
                onClick={() => { onChange(""); setQ(""); setOpen(false); }}
                className="text-[#647087] hover:text-white p-0.5"
                title="Clear"
                data-testid={`${testId}-clear`}
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
          <button
            type="button"
            onClick={() => { onChange(""); setQ(""); setOpen(false); }}
            className={`w-full text-left px-2 py-1.5 text-xs rounded hover:bg-white/[0.04] ${!value ? "text-[#1fb6c1]" : "text-[#a0aabf]"}`}
            data-testid={`${testId}-all`}
          >
            {allLabel}
          </button>
          {filtered.length === 0 ? (
            <div className="px-2 py-3 text-[10px] text-[#647087] text-center">No matches</div>
          ) : (
            (() => {
              let lastGroup = null;
              return filtered.map((o) => {
                const showGroup = o.group && o.group !== lastGroup;
                lastGroup = o.group;
                return (
                  <div key={o.id}>
                    {showGroup && (
                      <div className="text-[9px] uppercase tracking-wider text-[#647087] px-2 pt-2 pb-0.5">
                        {o.group}
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => { onChange(o.id); setOpen(false); setQ(""); }}
                      className={`w-full text-left px-2 py-1.5 text-xs rounded hover:bg-white/[0.04] truncate ${value === o.id ? "text-[#1fb6c1]" : "text-white"}`}
                      data-testid={`${testId}-opt-${o.id}`}
                    >
                      {o.label}
                    </button>
                  </div>
                );
              });
            })()
          )}
        </div>
      )}
    </div>
  );
}
